import { useNavigate } from 'react-router-dom';
import { Banner, Card, MonoLabel, Skeleton } from '../components/primitives';
import { useSettingsOverviewVM } from '../lib/vm/useSettingsOverviewVM';
import { formatRelativeTime } from '../lib/relativeTime';
import './Settings.css';

/**
 * `/settings` overview index (turn 3). Two-column responsive card grid
 * + compact org header strip. Cards are filtered by viewer permission role
 * (Owner sees 7 / Admin sees 6 / Member sees 5). Capability-denied cards
 * are hidden — no disabled-with-tooltip affordance in v1.
 *
 * Each card links to `/settings/<area>`. Those sub-routes ship in subsequent
 * Phase-2 turns; until then the link target 404s inside the shell, which is
 * acceptable per the spec ("clicking a stale bookmark to /settings/<area>
 * 404s in the sub-area, not here").
 */
export function Settings() {
  const vm = useSettingsOverviewVM();
  const navigate = useNavigate();

  const loading = vm.status === 'loading';
  const errored = vm.status === 'error';
  const empty = vm.status === 'empty';

  return (
    <section
      className="set-page"
      data-testid="settings-overview"
      data-viewer-role={vm.viewerRole ?? ''}
    >
      {loading ? (
        <div className="set-loading">
          <Skeleton height={56} className="set-loading-strip" />
          <div className="set-loading-grid">
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
          </div>
        </div>
      ) : null}

      {errored ? (
        <Banner kind="error" data-testid="settings-error">
          Couldn't load settings. Try again.
        </Banner>
      ) : null}

      {empty ? (
        <Banner kind="default" data-testid="settings-empty-org">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {vm.status === 'ready' ? (
        <>
          <header className="set-header" data-testid="settings-header">
            <h1 className="set-org-name">{vm.activeOrgName ?? 'Org'}</h1>
            <MonoLabel
              size="sm"
              tracking="wide"
              tone="muted"
              className="set-meta"
              data-testid="settings-meta"
            >
              {vm.memberCount} {vm.memberCount === 1 ? 'member' : 'members'}{' '}
              · {vm.documentCount}{' '}
              {vm.documentCount === 1 ? 'document' : 'documents'} · You:{' '}
              <span
                className="set-role-chip"
                data-testid="settings-role-chip"
              >
                {vm.viewerRoleLabel}
              </span>
            </MonoLabel>
          </header>

          {vm.cards.length === 0 ? (
            <p
              className="set-empty"
              data-testid="settings-no-readable-cards"
            >
              No settings available for your role.
            </p>
          ) : (
            <div className="set-grid" data-testid="settings-grid">
              {vm.cards.map((card) => {
                const updated =
                  card.lastUpdatedAt && card.lastUpdatedBy
                    ? `Updated ${formatRelativeTime(card.lastUpdatedAt)} by ${card.lastUpdatedBy}`
                    : 'Updated never';
                const never = !card.lastUpdatedAt;
                return (
                  <Card
                    key={card.key}
                    className="set-card"
                    role="link"
                    tabIndex={0}
                    aria-label={card.title}
                    data-testid={`settings-card-${card.key}`}
                    onClick={() =>
                      navigate(card.to, { state: { routeTransition: 'forward' } })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(card.to, {
                          state: { routeTransition: 'forward' },
                        });
                      }
                    }}
                  >
                    <div className="set-card-body">
                      <div className="set-card-title">
                        {card.title}
                        {card.ownerOnly ? (
                          <span className="set-card-owner-only">
                            {' · Owners only'}
                          </span>
                        ) : null}
                      </div>
                      <p className="set-card-desc">{card.description}</p>
                      <div
                        className={
                          never
                            ? 'set-card-updated set-card-updated-never'
                            : 'set-card-updated'
                        }
                        title={card.lastUpdatedAt ?? undefined}
                      >
                        {updated}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
